/**
 * ConnectedObservationDetailView - Connects observation details to application data.
 *
 * Responsibility:
 * - Display observation metadata (type, timestamp, model, environment, etc.)
 * - Show cost and token usage with tooltips
 * - Provide tabbed interface (Preview, Log View [v4 only], Scores)
 * - Support Formatted/JSON toggle for preview and log view content
 *
 * Hooks:
 * - useViewPreferences() - for JSON view preference
 * - useState() - for tab selection
 * - useReadPath() - for v4 mode detection (enables log tab)
 *
 * Re-renders when:
 * - Observation prop changes (new observation selected)
 * - Tab selection changes
 * - View mode toggle changes
 */

import { type ObservationReturnTypeWithMetadata } from "@/src/server/api/routers/traces";
import { cn } from "@/src/utils/tailwind";
import { useCallback, useMemo, useState } from "react";
import { Switch } from "@/src/components/design-system/Switch/Switch";
import { HoverCard } from "@/src/components/design-system/HoverCard/HoverCard";
import { Tabs } from "@/src/components/design-system/Tabs/Tabs";
import {
  CommentDrawerController,
  getCommentDrawerInitialStateFromUrl,
} from "@/src/features/comments/CommentDrawerController";
import { useCommentedPaths } from "@/src/features/comments/hooks/useCommentedPaths";
import { useRouter } from "next/router";
import { TraceDetailTabsBarList } from "../TraceDetailTabsBarList";
import { getDetailTabs } from "../../fns/getDetailTabs";
import { resolveDetailTab } from "../../fns/resolveDetailTab";
import { default as ScoresTable } from "@/src/features/scores/ScoresTable";
import { getMostRecentCorrection } from "@/src/features/corrections/utils/getMostRecentCorrection";
import { useJsonExpansion } from "@/src/features/traces/contexts/JsonExpansionProvider";
import { useMedia } from "@/src/features/traces/hooks/useMedia";
import {
  type DetailTab,
  useSelection,
} from "@/src/features/traces/contexts/SelectionProvider";
import { usePostHogClientCapture } from "@/src/features/posthog-analytics/usePostHogClientCapture";
import { useTraceAnalyticsDimensions } from "@/src/features/traces/hooks/useTraceAnalyticsDimensions";
import { useViewPreferences } from "@/src/features/traces/contexts/ViewPreferencesProvider";
import {
  jsonViewToggleTab,
  normalizeJsonViewPreference,
} from "@/src/components/ui/jsonViewPreference";

// Contexts and hooks
import { useTraceData } from "@/src/features/traces/contexts/TraceDataProvider";
import { useParsedObservation } from "@/src/features/traces/hooks/useParsedObservation";
import { api } from "@/src/utils/api";

// Extracted components
import { ObservationDetailViewHeader } from "@/src/features/traces/components/ObservationDetailView/components/ObservationDetailViewHeader/ObservationDetailViewHeader";
import { TraceLogView } from "../TraceLogView/TraceLogView";
import { useReadPath } from "@/src/features/events/hooks/useReadPath";
import { TRACE_VIEW_CONFIG } from "@/src/features/traces/constants/traceViewConfig";
import {
  aggregateTraceMetrics,
  getDescendantIds,
} from "@/src/features/traces/fns/traceAggregation";
import { useHasProjectAccess } from "@/src/features/rbac/utils/checkProjectAccess";
import { useSession } from "next-auth/react";
import { ObservationPreview } from "./ObservationPreview";
import type { IOPreviewPretty } from "../IOPreview/IOPreviewPretty";
import { useInternalFeaturesEnabled } from "@/src/features/feature-flags/hooks/useInternalFeaturesEnabled";
import { DetailAttributesTab } from "../DetailAttributesTab";
import { buildModelParameters } from "@/src/features/traces/fns/buildModelParameters";
import { buildObservationAttributes } from "@/src/features/traces/fns/buildObservationAttributes";

export interface ConnectedObservationDetailViewProps {
  observation: ObservationReturnTypeWithMetadata;
  projectId: string;
  traceId: string;
}

export function ConnectedObservationDetailView({
  observation,
  projectId,
  traceId,
}: ConnectedObservationDetailViewProps) {
  const router = useRouter();
  // Tab and view state from URL (via SelectionContext)
  const {
    selectedTab: globalSelectedTab,
    setSelectedTab: setGlobalSelectedTab,
  } = useSelection();
  const utils = api.useUtils();
  const capture = usePostHogClientCapture();
  const analyticsDimensions = useTraceAnalyticsDimensions();

  // V4 beta mode and observations for log tab
  const { isV4: isV4Enabled } = useReadPath();
  const internalFeaturesEnabled = useInternalFeaturesEnabled();
  const {
    observations,
    roots,
    nodeMap,
    traceLevelScoreOwnerIds,
    detachedObservationId,
    detachedObservationIsMisplaced,
  } = useTraceData();
  const isLogViewVirtualized =
    observations.length >= TRACE_VIEW_CONFIG.logView.virtualizationThreshold;
  // Get jsonViewPreference directly from ViewPreferencesContext for "json-beta" support
  const {
    jsonViewPreference,
    setJsonViewPreference,
    jsonBetaEnabled,
    setJsonBetaEnabled,
    isPeekMode,
    isAnnotationMode,
  } = useViewPreferences();

  // Tab visibility: hide Log View and Scores tabs in annotation mode
  const tabs = getDetailTabs({
    target: "observation",
    isV4: isV4Enabled,
    internalFeaturesEnabled,
    isAnnotationMode,
    hasObservations: observations.length > 0,
    canViewScores: true,
  });
  const showLogViewTab = tabs.includes("log");
  const showScoresTab = tabs.includes("scores");
  const attributes = buildObservationAttributes({
    model: observation.model,
    environment: observation.environment,
    release: observation.release,
    version: observation.version,
    sessionId: observation.sessionId,
    userId: observation.userId,
  });
  const modelParameters = buildModelParameters(observation.modelParameters);

  // for v4:
  // is this observation topmost in tree? we don't check for root observation here as this is not necessarily given.
  // Uses the tree's roots array which handles orphans correctly.
  // Both stay absent/false for an observation outside the loaded (capped) list:
  // subtree metrics and root-only chrome are tree facts we genuinely don't have,
  // so they are omitted rather than guessed. A merged-in row whose parent is also
  // past the cap sits AMONG the roots without being one — root chrome there would
  // assert a position we don't know.
  const treeNode = nodeMap.get(observation.id);
  const isRoot =
    roots.some((root) => root.id === observation.id) &&
    !(
      detachedObservationIsMisplaced && observation.id === detachedObservationId
    );

  // Without a TRACE row (v4) this span stands in for the trace, so its Scores
  // tab covers the trace-level scores.
  const ownsTraceLevelScores = traceLevelScoreOwnerIds.has(observation.id);

  // For root observations, compute subtree metrics for badge tooltips.
  // We compute this lazily here rather than in tree-building.ts because:
  // - TreeNode.totalCost just has the aggregated cost, we use it
  // - costDetails/usageDetails (for tooltips) aren't in TreeNode, adding them causes high memory for all nodes, esp on big traces
  // - computation only runs when viewing a root observation and is memo'd
  const subtreeMetrics = useMemo(() => {
    if (!isRoot || !treeNode) return null;
    const descendantIds = getDescendantIds(treeNode);
    const descendantIdSet = new Set(descendantIds);

    const descendants = observations.filter((obs) =>
      descendantIdSet.has(obs.id),
    );
    const allObservations = [observation, ...descendants];
    return aggregateTraceMetrics(allObservations);
  }, [isRoot, treeNode, observations, observation]);

  const selectedTab = resolveDetailTab(globalSelectedTab, tabs);

  const refreshTraceScores = useCallback(() => {
    utils.traces.byIdWithObservationsAndScores.invalidate({
      projectId,
      traceId,
    });
    utils.events.scoresForTrace.invalidate({
      projectId,
      traceId,
    });
  }, [projectId, traceId, utils]);

  const setSelectedTab = (tab: DetailTab) => {
    if (tab === "scores") {
      refreshTraceScores();
    }
    if (tab !== selectedTab) {
      capture("trace_detail:detail_tab_switch", {
        tab,
        target: "observation",
        ...analyticsDimensions,
      });
    }
    setGlobalSelectedTab(tab);
  };

  // Map jsonViewPreference to currentView format expected by child components
  const currentView = jsonViewPreference;
  const selectedViewTab = jsonViewToggleTab(currentView);
  const [isPrettyViewAvailable, setIsPrettyViewAvailable] = useState(true);

  const handleViewTabChange = useCallback(
    (tab: string) => {
      if (selectedTab === "log") {
        capture("trace_detail:log_view_interaction", {
          action: "view_mode_switch",
          target: "observation",
          mode: tab,
          ...analyticsDimensions,
        });
      }
      if (tab === "json") {
        // When switching to JSON, use beta preference
        setJsonViewPreference(jsonBetaEnabled ? "json-beta" : "json");
      } else {
        setJsonViewPreference(normalizeJsonViewPreference(tab));
      }
    },
    [
      jsonBetaEnabled,
      setJsonViewPreference,
      selectedTab,
      capture,
      analyticsDimensions,
    ],
  );

  const handleBetaToggle = useCallback(
    (enabled: boolean) => {
      capture("trace_detail:json_beta_toggle", {
        enabled,
        target: "observation",
        ...analyticsDimensions,
      });
      setJsonBetaEnabled(enabled);
      setJsonViewPreference(enabled ? "json-beta" : "json");
    },
    [setJsonBetaEnabled, setJsonViewPreference, capture, analyticsDimensions],
  );

  // Get comments, scores, corrections, and expansion state from contexts
  const { comments, serverScores: scores, corrections } = useTraceData();
  const {
    formattedExpansion,
    setFormattedFieldExpansion,
    jsonExpansion,
    setJsonFieldExpansion,
    advancedJsonExpansion,
    setAdvancedJsonExpansion,
  } = useJsonExpansion();
  const observationScores = useMemo(
    () => scores.filter((s) => s.observationId === observation.id),
    [scores, observation.id],
  );
  const observationCorrections = useMemo(
    () => corrections.filter((c) => c.observationId === observation.id),
    [corrections, observation.id],
  );

  const outputCorrection = getMostRecentCorrection(observationCorrections);

  // Fetch and parse observation input/output in background (Web Worker)
  // This combines tRPC fetch + non-blocking JSON parsing
  const {
    observation: observationWithIORaw,
    parsedInput,
    parsedOutput,
    parsedMetadata,
    isLoadingObservation,
    isWaitingForParsing,
  } = useParsedObservation({
    observationId: observation.id,
    traceId: traceId,
    projectId: projectId,
    startTime: observation.startTime,
    baseObservation: observation,
  });

  // Type narrowing: when baseObservation is provided, result has full observation fields
  // (EventBatchIOOutput case only occurs when baseObservation is missing)
  const observationWithIO =
    observationWithIORaw && "type" in observationWithIORaw
      ? observationWithIORaw
      : undefined;

  // For backward compatibility, create observationWithIO query-like object
  const observationWithIOCompat = {
    data: observationWithIO,
    isLoading: isLoadingObservation,
  };

  // Fetch media for this observation
  const observationMedia = useMedia({
    projectId,
    traceId,
    observationId: observation.id,
  });

  const session = useSession();
  const hasCommentsReadAccess = useHasProjectAccess({
    projectId,
    scope: "comments:read",
  });
  const observationComments = api.comments.getByObjectId.useQuery(
    {
      projectId,
      objectId: observation.id,
      objectType: "OBSERVATION",
    },
    {
      refetchOnMount: false,
      enabled: hasCommentsReadAccess && session.status === "authenticated",
    },
  );

  const commentedPathsByField = useCommentedPaths(observationComments.data);

  // Calculate latency in seconds if not provided
  const latencySeconds = useMemo(() => {
    if (observation.latency) {
      return observation.latency;
    }
    if (observation.startTime && observation.endTime) {
      return (
        (observation.endTime.getTime() - observation.startTime.getTime()) / 1000
      );
    }
    return null;
  }, [observation.latency, observation.startTime, observation.endTime]);

  const previewSkills = getPreviewSkills({
    skillManagementEnabled: isV4Enabled,
    observationType: observation.type,
    skillsAvailable: observationWithIO?.skillsAvailable,
    skillsResourceLoaded: observationWithIO?.skillsResourceLoaded,
  });

  return (
    <CommentDrawerController
      projectId={projectId}
      initialState={() => getCommentDrawerInitialStateFromUrl(router.query)}
      count={comments.get(observation.id)}
    >
      {({ disabled, openDrawer }) => (
        <div className="flex h-full flex-col overflow-hidden">
          <ObservationDetailViewHeader
            observation={observation}
            observationWithIO={observationWithIO}
            projectId={projectId}
            traceId={traceId}
            latencySeconds={latencySeconds}
            observationScores={observationScores}
            commentCount={comments.get(observation.id)}
            commentDrawerControl={{
              disabled,
              openDrawer: () =>
                openDrawer({
                  type: "comments",
                  objectId: observation.id,
                  objectType: "OBSERVATION",
                  objectStartTime: observation.startTime,
                }),
            }}
            subtreeMetrics={subtreeMetrics}
            treeNodeTotalCost={treeNode?.totalCost}
          />

          <Tabs
            value={selectedTab}
            layout="fill"
            onValueChange={(value) => setSelectedTab(value as DetailTab)}
          >
            <TraceDetailTabsBarList
              tabs={tabs}
              logViewDescription={
                isLogViewVirtualized
                  ? `Shows all ${observations.length} observations with virtualization enabled.`
                  : "Shows all observations concatenated. Great for quickly scanning through them."
              }
              trailingControls={
                /* View toggle where it applies; invisible on other tabs so the row keeps its width. */
                <div
                  className={cn(
                    "flex shrink-0 items-center",
                    !(
                      selectedTab === "log" ||
                      selectedTab === "attributes" ||
                      (selectedTab === "preview" && isPrettyViewAvailable)
                    ) && "invisible",
                  )}
                >
                  {/* Stays mounted so the row keeps its width; attributes and the virtualized log have no Beta view. */}
                  {selectedViewTab === "json" && (
                    <div
                      className={cn(
                        "flex items-center gap-1.5",
                        (selectedTab === "attributes" ||
                          (selectedTab === "log" && isLogViewVirtualized)) &&
                          "invisible",
                      )}
                    >
                      <Switch
                        size="sm"
                        checked={jsonBetaEnabled}
                        onCheckedChange={handleBetaToggle}
                      />
                      <span className="text-muted-foreground text-xs">
                        Optimized
                      </span>
                    </div>
                  )}
                  <div className="h-fit shrink-0 py-0.5 pr-4 pl-2">
                    <Tabs
                      value={
                        selectedTab === "log" && isLogViewVirtualized
                          ? "pretty"
                          : selectedViewTab
                      }
                      onValueChange={(value) => {
                        if (
                          selectedTab === "log" &&
                          isLogViewVirtualized &&
                          value === "json"
                        ) {
                          return;
                        }
                        handleViewTabChange(value);
                      }}
                    >
                      <Tabs.List variant="inset" size="sm">
                        <Tabs.Trigger value="pretty" label="Formatted" />
                        {selectedTab === "log" && isLogViewVirtualized ? (
                          <HoverCard
                            openDelay={200}
                            sideOffset={8}
                            placement="bottom-end"
                            content={
                              <div className="w-64 p-3 text-sm">
                                <p className="font-bold">
                                  Raw view unavailable
                                </p>
                                <p className="text-muted-foreground mt-1">
                                  Disabled for traces with{" "}
                                  {
                                    TRACE_VIEW_CONFIG.logView
                                      .virtualizationThreshold
                                  }
                                  + observations to maintain performance.
                                </p>
                              </div>
                            }
                          >
                            {({ getTriggerProps }) => (
                              <span tabIndex={0} {...getTriggerProps()}>
                                <Tabs.Trigger
                                  value="json"
                                  disabled
                                  label="Raw"
                                />
                              </span>
                            )}
                          </HoverCard>
                        ) : (
                          <Tabs.Trigger value="json" label="Raw" />
                        )}
                      </Tabs.List>
                    </Tabs>
                  </div>
                </div>
              }
            />

            <Tabs.Content value="preview" layout="fill">
              <ObservationPreview
                currentView={currentView}
                previewKey={observation.id}
                onPrettyViewAvailabilityChange={setIsPrettyViewAvailable}
                previewProps={{
                  observationName: observation.name ?? undefined,
                  observationType: observation.type,
                  skills: previewSkills,
                  input: observationWithIOCompat.data?.input ?? undefined,
                  output: observationWithIOCompat.data?.output ?? undefined,
                  status: observation.statusMessage
                    ? {
                        level: observation.level,
                        message: observation.statusMessage,
                      }
                    : undefined,
                  outputCorrection,
                  metadata: observationWithIOCompat.data?.metadata ?? undefined,
                  parsedInput,
                  parsedOutput,
                  parsedMetadata,
                  isLoading: observationWithIOCompat.isLoading,
                  isParsing: isWaitingForParsing,
                  media: observationMedia.data,
                  inputExpansionState: formattedExpansion.input,
                  outputExpansionState: formattedExpansion.output,
                  metadataExpansionState: formattedExpansion.metadata,
                  onInputExpansionChange: (exp) =>
                    setFormattedFieldExpansion(
                      "input",
                      exp as Record<string, boolean>,
                    ),
                  onOutputExpansionChange: (exp) =>
                    setFormattedFieldExpansion(
                      "output",
                      exp as Record<string, boolean>,
                    ),
                  onMetadataExpansionChange: (exp) =>
                    setFormattedFieldExpansion(
                      "metadata",
                      exp as Record<string, boolean>,
                    ),
                  advancedJsonExpansionState: advancedJsonExpansion,
                  onAdvancedJsonExpansionChange: setAdvancedJsonExpansion,
                  jsonInputExpanded: jsonExpansion.input,
                  jsonOutputExpanded: jsonExpansion.output,
                  jsonMetadataExpanded: jsonExpansion.metadata,
                  onJsonInputExpandedChange: (expanded) =>
                    setJsonFieldExpansion("input", expanded),
                  onJsonOutputExpandedChange: (expanded) =>
                    setJsonFieldExpansion("output", expanded),
                  onJsonMetadataExpandedChange: (expanded) =>
                    setJsonFieldExpansion("metadata", expanded),
                  enableInlineComments: true,
                  onAddInlineComment: (selection) =>
                    openDrawer({
                      type: "inline-comment",
                      selection,
                      objectId: observation.id,
                      objectType: "OBSERVATION",
                      objectStartTime: observation.startTime,
                    }),
                  commentedPathsByField,
                  observationId: observation.id,
                  projectId,
                  traceId,
                  environment: observation.environment,
                }}
              />
            </Tabs.Content>

            <Tabs.Content value="attributes" layout="fill">
              <DetailAttributesTab
                observation={{
                  attributes,
                  attributesAnchorTime: observation.startTime,
                  modelParameters,
                }}
                metadata={observationWithIOCompat.data?.metadata ?? undefined}
                parsedMetadata={parsedMetadata}
                objectId={observation.id}
                projectId={projectId}
                currentView={selectedViewTab}
              />
            </Tabs.Content>

            {showScoresTab ? (
              <Tabs.Content value="scores" layout="fill">
                <div className="flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden">
                  <ScoresTable
                    projectId={projectId}
                    traceId={traceId}
                    observationId={observation.id}
                    includeTraceLevelScores={ownsTraceLevelScores}
                    hiddenColumns={[
                      "traceId",
                      "observationId",
                      "traceName",
                      "traceTags",
                      "jobConfigurationId",
                      "userId",
                    ]}
                    localStorageSuffix="ObservationPreview"
                    insetToolbar
                    disableUrlPersistence={isPeekMode || isAnnotationMode}
                  />
                </div>
              </Tabs.Content>
            ) : null}

            {showLogViewTab ? (
              <Tabs.Content value="log" layout="fill">
                <TraceLogView
                  traceId={traceId}
                  projectId={projectId}
                  currentView={isLogViewVirtualized ? "pretty" : currentView}
                  target="observation"
                />
              </Tabs.Content>
            ) : null}
          </Tabs>
        </div>
      )}
    </CommentDrawerController>
  );
}

function getPreviewSkills({
  skillManagementEnabled,
  observationType,
  skillsAvailable,
  skillsResourceLoaded,
}: PreviewSkillsInput): React.ComponentProps<typeof IOPreviewPretty>["skills"] {
  if (!skillManagementEnabled) return undefined;
  if (observationType === "GENERATION") {
    return { kind: "available", items: skillsAvailable ?? [] };
  }
  if (observationType === "TOOL") {
    return { kind: "loaded", items: skillsResourceLoaded ?? [] };
  }
  return undefined;
}

type PreviewSkillsInput = {
  skillManagementEnabled: boolean;
  observationType: ObservationReturnTypeWithMetadata["type"];
  skillsAvailable:
    | ObservationReturnTypeWithMetadata["skillsAvailable"]
    | undefined;
  skillsResourceLoaded:
    | ObservationReturnTypeWithMetadata["skillsResourceLoaded"]
    | undefined;
};
